import React, { useMemo, useState } from "react";
import DonutChart from "../../Components/Charts/DonutChart";
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  FilterIcon,
  GridIcon,
  HistoryIcon,
  MoreIcon,
  PlugConnectIcon,
  PlusIcon,
  SearchIcon,
  SettingsIcon,
  XCircleIcon,
} from "../../Components/Icons";
import {
  DATA_SOURCES,
  INGESTION_ACTIVITY,
  INGESTION_HEALTH,
  SOURCE_QUICK_ACTIONS,
  SOURCE_STATS,
  SOURCE_STATUSES,
  SOURCE_TYPES,
  TOTAL_SOURCES,
  TOTAL_TYPE_SOURCES,
  TYPE_DISTRIBUTION,
} from "./data";
import { Pager, SOURCE_ICONS, StatCards, chipClass } from "./shared";
import "./DataIntegration.css";

const ACTION_ICONS = {
  plus: PlusIcon,
  plug: PlugConnectIcon,
  grid: GridIcon,
  settings: SettingsIcon,
};

const ACTIVITY_ICONS = {
  Success: CheckCircleIcon,
  Warning: AlertTriangleIcon,
  Failed: XCircleIcon,
};

export default function DataSources() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return DATA_SOURCES.filter((source) => {
      if (term && !`${source.name} ${source.code}`.toLowerCase().includes(term)) {
        return false;
      }
      if (status && source.status !== status) return false;
      if (type && source.type !== type) return false;
      return true;
    });
  }, [search, status, type]);

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Data Sources</h1>
          <p className="page__subtitle">
            Manage and monitor all external and internal data sources integrated
            with DGI Smart Tax Registry.
          </p>
        </div>
        <div className="page__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost">
            <HistoryIcon size={17} />
            Ingestion History
          </button>
          <button type="button" className="dgi-btn dgi-btn--primary">
            <PlusIcon size={17} />
            Add New Source
          </button>
        </div>
      </div>

      <StatCards cards={SOURCE_STATS} />

      <div className="di-main">
        <section className="dgi-card">
          <div className="dgi-card__header">
            <h2 className="dgi-card__title">Data Sources List</h2>
          </div>

          <div className="di-filters">
            <div className="filters__search">
              <input
                type="search"
                placeholder="Search data sources..."
                aria-label="Search data sources"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
              <SearchIcon size={17} />
            </div>
            <span className="select select--block">
              <select
                value={status}
                aria-label="Filter by status"
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="">All Statuses</option>
                {SOURCE_STATUSES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDownIcon size={16} />
            </span>
            <span className="select select--block">
              <select
                value={type}
                aria-label="Filter by source type"
                onChange={(event) => setType(event.target.value)}
              >
                <option value="">All Source Types</option>
                {SOURCE_TYPES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <ChevronDownIcon size={16} />
            </span>
            <button type="button" className="dgi-btn dgi-btn--ghost dgi-btn--sm">
              <FilterIcon size={16} />
              Filters
            </button>
          </div>

          <div className="dgi-table-wrap">
            <table className="dgi-table dgi-table--sources">
              <thead>
                <tr>
                  <th>Source Name</th>
                  <th>Source Type</th>
                  <th>Owner</th>
                  <th>Frequency</th>
                  <th>Last Ingestion</th>
                  <th>Status</th>
                  <th>Records</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((source) => {
                  const Icon = SOURCE_ICONS[source.icon];
                  return (
                    <tr key={source.code}>
                      <td>
                        <span className="src">
                          <span className={`src__icon kpi__icon--${source.tone}`}>
                            <Icon size={17} />
                          </span>
                          <span className="src__text">
                            <button type="button" className="link">
                              {source.name}
                            </button>
                            <span className="dgi-table__sub">{source.code}</span>
                          </span>
                        </span>
                      </td>
                      <td>
                        <span className={chipClass(source.type)}>
                          {source.type}
                        </span>
                      </td>
                      <td>{source.owner}</td>
                      <td>{source.frequency}</td>
                      <td>
                        {source.date}
                        <span className="dgi-table__sub">{source.time}</span>
                      </td>
                      <td>
                        <span
                          className={`chip chip--dot ${chipClass(source.status)}`}
                        >
                          {source.status}
                        </span>
                      </td>
                      <td>{source.records}</td>
                      <td>
                        <button
                          type="button"
                          className="dgi-card__more"
                          aria-label={`Actions for ${source.name}`}
                        >
                          <MoreIcon size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {rows.length === 0 && (
            <p className="dgi-table__empty">No data sources match these filters.</p>
          )}

          <Pager
            page={page}
            setPage={setPage}
            perPage={perPage}
            setPerPage={setPerPage}
            total={TOTAL_SOURCES}
            shown={rows.length}
            noun="data sources"
          />
        </section>

        <div className="stack">
          <section className="dgi-card">
            <div className="dgi-card__header">
              <h2 className="dgi-card__title">Source Type Distribution</h2>
            </div>
            <DonutChart
              segments={TYPE_DISTRIBUTION}
              total={TOTAL_TYPE_SOURCES}
              totalLabel="Total Sources"
            />
            <button type="button" className="dgi-card__link">
              View all sources by type <ArrowRightIcon size={16} />
            </button>
          </section>

          <section className="dgi-card">
            <div className="dgi-card__header">
              <h2 className="dgi-card__title">Ingestion Health (This Month)</h2>
            </div>
            <ul className="health">
              {INGESTION_HEALTH.map((row) => (
                <li className="health__row" key={row.label}>
                  <span
                    className="donut__swatch"
                    style={{ background: row.color }}
                  />
                  <span className="health__label">{row.label}</span>
                  <span className="health__count">
                    {row.count} <i>({row.share}%)</i>
                  </span>
                  <span className="score__track health__track">
                    <span
                      className="score__fill"
                      style={{ width: `${row.share}%`, background: row.color }}
                    />
                  </span>
                </li>
              ))}
            </ul>
            <button type="button" className="dgi-card__link">
              View ingestion history <ArrowRightIcon size={16} />
            </button>
          </section>
        </div>
      </div>

      <div className="di-main">
        <section className="dgi-card">
          <div className="dgi-card__header">
            <h2 className="dgi-card__title">Recent Ingestion Activity</h2>
          </div>
          <ul className="activity">
            {INGESTION_ACTIVITY.map((entry) => {
              const Icon = ACTIVITY_ICONS[entry.status] ?? AlertCircleIcon;
              return (
                <li className="activity__row" key={entry.id}>
                  <span
                    className={`activity__icon activity__icon--${entry.status.toLowerCase()}`}
                  >
                    <Icon size={18} />
                  </span>
                  <span className="activity__time">{entry.time}</span>
                  <span className="activity__source">{entry.source}</span>
                  <span className="activity__mode">{entry.mode}</span>
                  <span className="activity__records">{entry.records}</span>
                  <span className={chipClass(entry.status)}>{entry.status}</span>
                  <span className="activity__duration">{entry.duration}</span>
                </li>
              );
            })}
          </ul>
          <button type="button" className="dgi-card__link dgi-card__link--right">
            View all ingestion history <ArrowRightIcon size={16} />
          </button>
        </section>

        <section className="dgi-card">
          <div className="dgi-card__header">
            <h2 className="dgi-card__title">Data Source Quick Actions</h2>
          </div>
          <ul className="qactions">
            {SOURCE_QUICK_ACTIONS.map((action) => {
              const Icon = ACTION_ICONS[action.icon];
              return (
                <li key={action.key}>
                  <button type="button" className="qaction">
                    <span className="qaction__icon">
                      <Icon size={18} />
                    </span>
                    <span>
                      <span className="qaction__title">{action.title}</span>
                      <span className="qaction__text">{action.text}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </div>
  );
}
